/**
 * What the interface does with a live event.
 *
 * The stream itself is in `lib/realtime.ts`; this is the translation layer.
 * Every event does exactly two things:
 *
 *   1. marks the affected React Query caches stale, so the worklist, the inbox
 *      badge and the dashboard redraw with fresh data. The event is never used
 *      as the data - it names what changed and the client asks the API for it,
 *      through the same permission checks as any other request.
 *   2. raises a notice, but only for `notification` events. A stage entry emits
 *      both a `task.assigned` (addressed to a posting, for the caches) and a
 *      notification row (addressed to a person, with wording somebody wrote), so
 *      toasting both would show the same arrival twice.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";

import {
  getRealtimeStatus,
  subscribeRealtime,
  subscribeRealtimeStatus,
  type RealtimeEvent,
  type RealtimeStatus,
} from "@/lib/realtime";

export type { RealtimeStatus };

export interface LiveNotice {
  id: number;
  title: string;
  body?: string;
  severity: "info" | "warning" | "critical";
  /** Where the notice points. Absent when the event names nothing navigable. */
  href?: string;
  /**
   * The record this notice is about, when it names one. Used to collapse the
   * several notifications one action can raise about the same thing.
   */
  subject?: string;
}

/** Queries every event invalidates, whatever it was about. */
const ALWAYS: string[][] = [["inbox"], ["me"], ["dashboard"], ["workflow"], ["notifications"]];

/**
 * Queries an event about one kind of record invalidates.
 *
 * Deliberately broader than the record itself: an analysis reaching review
 * changes the analysis, the sample it belongs to and the request above that, and
 * a list showing a stale count is the same bug as a list showing a stale row.
 */
const BY_ENTITY: Record<string, string[][]> = {
  test_request: [["requests"], ["request-deletions"]],
  sample: [["samples"], ["requests"], ["discrepancies"]],
  sample_test: [["analyses"], ["samples"], ["acquisition"]],
  certificate: [["certificates"], ["requests"]],
  equipment_calibration: [["equipment"]],
  method_validation: [["catalog"]],
  delegation: [["delegations"]],
  sample_disposal: [["sample-disposals"], ["samples"], ["requests"]],
};

/** Where a record of each kind lives. Types with no screen of their own are absent. */
const ROUTES: Record<string, (id: string) => string> = {
  test_request: (id) => `/requests/${id}`,
  sample_test: (id) => `/analyses/${id}`,
  certificate: (id) => `/certificates/${id}`,
  // The register, not the row: a delegation has no page of its own, and the
  // table is small enough that landing on it is landing on the delegation.
  delegation: () => "/delegations",
  // The inbox, opened on the message the notice is about.
  customer_feedback: (id) => `/feedback/${id}`,
};

/** Notices on screen at once. Older ones fall off the top rather than stacking. */
const MAX_NOTICES = 4;

/**
 * How long invalidations are collected before being applied. Approving a request
 * with nine analyses on it emits ten events in the same breath; without this the
 * browser fires ten identical refetches.
 */
const COALESCE_MS = 250;

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

function asText(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/** The record an event is about, whichever shape the event uses to say so. */
function subject(event: RealtimeEvent): {
  type?: string;
  id?: string;
  code?: string;
} {
  if (event.kind === "notification") {
    const target = asRecord(event.payload.target);
    return {
      type: asText(target.type),
      id: asText(target.id),
      code: asText(target.code),
    };
  }
  return {
    type: asText(event.payload.entity_type),
    id: asText(event.payload.entity_id),
    code: asText(event.payload.entity_code),
  };
}

function hrefFor(type: string | undefined, id: string | undefined): string | undefined {
  if (!type || !id) return undefined;
  return ROUTES[type]?.(id);
}

function severityOf(value: unknown): LiveNotice["severity"] {
  return value === "warning" || value === "critical" ? value : "info";
}

function noticeFor(event: RealtimeEvent): Omit<LiveNotice, "id"> | null {
  if (event.kind !== "notification") return null;
  const title = asText(event.payload.title);
  if (!title) return null;
  const { type, id } = subject(event);
  return {
    title,
    body: asText(event.payload.body),
    severity: severityOf(event.payload.severity),
    href: hrefFor(type, id),
    subject: type && id ? `${type}:${id}` : undefined,
  };
}

function keysFor(event: RealtimeEvent): string[][] {
  const { type } = subject(event);
  return type ? [...ALWAYS, ...(BY_ENTITY[type] ?? [])] : ALWAYS;
}

/** Whether the stream is currently carrying events. */
export function useRealtimeStatus(): RealtimeStatus {
  return useSyncExternalStore(subscribeRealtimeStatus, getRealtimeStatus, getRealtimeStatus);
}

/**
 * The refetch interval for a query that polled before the stream existed.
 *
 * Two values, because two situations. With no stream the poll is the only thing
 * keeping the screen current, so it stays brisk. With a live stream most queries
 * need no poll at all - `false` - since an event arrives within milliseconds of
 * the change. A list that also shows work belonging to other people is the
 * exception: it changes for reasons nobody addressed to this viewer, so it takes
 * a slow backstop instead of nothing.
 */
export function useRefetchInterval(
  whenOffline: number,
  whenLive: number | false = false,
): number | false {
  return useRealtimeStatus() === "live" ? whenLive : whenOffline;
}

/**
 * Open the stream and keep the caches honest. Called once, from the shell.
 *
 * Returns the notices to render. They are owned here rather than in a store
 * because they are the shell's business: nothing else needs to read them, and
 * unmounting the shell - signing out - should take them with it.
 *
 * `enabled` is for the sessions that exist but may not work yet - an account
 * still on a temporary password, which the API refuses for everything but the
 * password change. Connecting there would retry a refusal in the background.
 */
export function useRealtimeStream(enabled = true): {
  notices: LiveNotice[];
  dismiss: (id: number) => void;
} {
  const queryClient = useQueryClient();
  const [notices, setNotices] = useState<LiveNotice[]>([]);
  const lastId = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    const pending = new Map<string, string[]>();
    let timer: ReturnType<typeof setTimeout> | null = null;

    const applyPending = () => {
      timer = null;
      const keys = [...pending.values()];
      pending.clear();
      keys.forEach((queryKey) => void queryClient.invalidateQueries({ queryKey }));
    };

    const unsubscribe = subscribeRealtime((event) => {
      keysFor(event).forEach((key) => pending.set(key.join("/"), key));
      if (timer === null) timer = setTimeout(applyPending, COALESCE_MS);

      const notice = noticeFor(event);
      if (notice === null) return;
      lastId.current += 1;
      const next = { ...notice, id: lastId.current };
      setNotices((current) => {
        // One action can raise more than one notification about the same record -
        // the workflow stage announcing itself and the handler that moved it both
        // write a row - and two notices side by side saying the same thing reads
        // as a bug. The later wording replaces the earlier one rather than
        // stacking beneath it; both rows are still in the bell.
        const withoutSubject = next.subject
          ? current.filter((notice) => notice.subject !== next.subject)
          : current;
        return [...withoutSubject, next].slice(-MAX_NOTICES);
      });
    });

    return () => {
      unsubscribe();
      if (timer !== null) clearTimeout(timer);
    };
  }, [queryClient, enabled]);

  const dismiss = useCallback((id: number) => {
    setNotices((current) => current.filter((notice) => notice.id !== id));
  }, []);

  return { notices, dismiss };
}
